(function () {
  'use strict';

  // ── LAYER 3: the reward APPLICATOR — issues the resolved offer's reward ──
  //
  // The offer object is only real if its reward actually happens in cart +
  // checkout. This is the ONE place that turns window.__activeOffer.reward into a
  // store action, via the two primitives that already exist in this theme:
  //   - apply a code  -> /checkout?discount=CODE   (discount_code, bxgy_code, gwp_coded)
  //   - add a variant -> /cart/add.js              (gwp_free_variant, gwp_coded)
  //
  // WRITE DISCIPLINE (2026-08-11 sweep): all cart writes flow through syncAll —
  // sequential, response-verified (fetch resolves on 4xx/429, so every response is
  // ok-checked), at most ONE cart:refresh per pass, and a consecutive-failure
  // breaker so a persistently-failing write (sold-out gift variant, deleted code,
  // rate limiting) can never become a request loop. Individual writers remain
  // callable (tests, targeted use) and then manage their own refresh.
  //
  // Reward types that carry a code the theme applies at checkout:
  var CODE_REWARD_TYPES = { discount_code: 1, bxgy_code: 1, gwp_coded: 1 };

  // Live-probed (2026-08-10): /discount/CODE APPENDS to the cart's discount_codes
  // array (multi-code carts) — it does NOT replace. The working removal is
  // POST /cart/update.js {discount: ""} which empties the array. So clearing =
  // update.js, arming = /discount/, switching = clear then arm.

  function assertOk(r) {
    if (!r || r.ok === false) throw new Error('cart write failed' + (r && r.status ? ' (' + r.status + ')' : ''));
    return r;
  }

  function refreshCart() {
    if (typeof document !== 'undefined') document.dispatchEvent(new Event('cart:refresh'));
  }
  function wantRefresh(opts) {
    return !(opts && opts.refresh === false);
  }

  // Count the set pieces that BELONG TO the given offer (sweep H1: a builder set
  // and a preconfig set in the same cart must not inflate each other's counts).
  // Exact-trigger offer -> only lines carrying ITS stamp; the generic builder ->
  // every _bundle line EXCEPT ones stamped for a registered preconfig offer.
  // No offer given -> legacy behavior (all _bundle lines).
  function bundleQty(items, offer) {
    var t = offer && offer.trigger;
    var exact = t && t.type === 'line_property' && t.value != null ? String(t.value) : null;
    var preconfig = null;
    if (offer && exact == null) {
      preconfig = {};
      ((typeof window !== 'undefined' && window.OfferRegistry) || []).forEach(function (o) {
        var ot = o.trigger || {};
        if (ot.type === 'line_property' && ot.key === '_bundle' && ot.value != null) preconfig[String(ot.value)] = 1;
      });
    }
    var n = 0;
    (items || []).forEach(function (i) {
      var v = (i.properties || {})._bundle;
      if (!v) return;
      if (exact != null) { if (String(v) !== exact) return; }
      else if (preconfig && preconfig[String(v)]) return;
      n += i.quantity || 1;
    });
    return n;
  }

  // The tier earned at a piece count: highest tier whose qty threshold is met.
  function tierFor(offer, qty) {
    var tiers = (offer && offer.reward && offer.reward.tiers) || [];
    var best = null;
    tiers.forEach(function (t) {
      if (qty >= (t.qty || 0) && (!best || (t.qty || 0) > (best.qty || 0))) best = t;
    });
    return best;
  }

  // Every code the offer system manages (reward.code + all tier codes, all offers).
  // The system arms/clears ONLY these — a customer-entered code is never touched.
  function managedCodes() {
    var out = {};
    ((typeof window !== 'undefined' && window.OfferRegistry) || []).forEach(function (o) {
      var r = o.reward || {};
      if (r.code) out[r.code] = 1;
      (r.tiers || []).forEach(function (t) { if (t.code) out[t.code] = 1; });
    });
    return out;
  }

  function activeOfferCode(cart) {
    var offer = (typeof window !== 'undefined') && window.__activeOffer;
    if (!offer || !offer.reward) return '';
    if (!CODE_REWARD_TYPES[offer.reward.type]) return '';
    // Tiered offer: the EARNED tier's code, from the live count of set pieces —
    // recomputed on every cart change, so editing the set down honestly drops or
    // downgrades the discount (the margin-leak fix). No tier earned -> no code.
    var tiers = offer.reward.tiers || [];
    if (tiers.length) {
      var t = tierFor(offer, bundleQty(cart && cart.items, offer));
      return t && t.code ? String(t.code).trim() : '';
    }
    var code = offer.reward.code;
    // Blank code = the discount is a server-side automatic (e.g. Regios reads the
    // _bundle_tier line property) — the theme issues NOTHING; the app applies it.
    return (typeof code === 'string' && code.trim()) ? code.trim() : '';
  }

  // Back-compat: the legacy milestone gift stamps _discount_code on the gift line.
  // Until the gift is a first-class offer reward, honour it when no active-offer
  // code is present. (Suppression means a bundle offer + a gift code never coexist.)
  function legacyGiftCode(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var p = items[i].properties || {};
      if (p._gift_item === 'true' && p._discount_code) return String(p._discount_code);
    }
    return '';
  }

  // The single source of the checkout code to carry for THIS cart/session.
  function checkoutCode(cart) {
    return activeOfferCode(cart) || legacyGiftCode(cart);
  }

  // The checkout URL, with the resolved offer's code applied when there is one.
  function checkoutUrl(cart) {
    var code = checkoutCode(cart);
    return code ? '/checkout?discount=' + encodeURIComponent(code) : '/checkout';
  }

  function codeInCart(cart, code) {
    return ((cart && cart.discount_codes) || []).some(function (d) { return d && d.code === code; });
  }

  function clearCartDiscounts() {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: '' })
    }).then(assertOk);
  }

  // Sync the cart's discount codes to the EARNED state (tier-aware, not just at
  // checkout) so cart.js — and therefore the drawer — always shows the truth:
  //   earned code missing         -> arm it; clear OUR stale codes first (codes
  //   earned tier changed            append, they don't replace — live-probed)
  //   nothing earned but one of   -> clear (update.js {discount:""}) — the
  //   OUR codes is still applied     margin-leak fix: edit below tier, discount drops
  // Only codes in managedCodes() are ever touched — a cart carrying a customer-
  // entered code is not ours to clear. Idempotent -> converges. Belt+suspenders:
  // the codes ALSO carry server-side min-quantity floors (SETTEST15 3+, SETTEST20
  // 4+), so even a missed sync can't discount a below-tier cart.
  var isSyncingDiscount = false;
  function syncCartDiscount(cart, opts) {
    if (typeof fetch !== 'function' || isSyncingDiscount) return Promise.resolve(false);
    var desired = activeOfferCode(cart);
    var applied = ((cart && cart.discount_codes) || []).map(function (d) { return d && d.code; });
    var managed = managedCodes();
    var staleManaged = applied.filter(function (c) { return managed[c] && c !== desired; });
    var hasCustomerCode = applied.some(function (c) { return c && !managed[c]; });

    var needArm = desired && applied.indexOf(desired) === -1;
    // never clear a cart holding a customer-entered code (clearing is all-or-nothing)
    var needClear = staleManaged.length > 0 && !hasCustomerCode;
    if (!needArm && !needClear) return Promise.resolve(false);

    isSyncingDiscount = true;
    var mutated = false;
    var chain = Promise.resolve();
    if (needClear) chain = chain.then(function () { return clearCartDiscounts().then(function () { mutated = true; }); });
    if (needArm) {
      chain = chain.then(function () {
        return fetch('/discount/' + encodeURIComponent(desired), { credentials: 'same-origin' })
          .then(assertOk)
          .then(function () { mutated = true; });
      });
    }
    return chain
      .then(function () {
        isSyncingDiscount = false;
        if (wantRefresh(opts)) refreshCart();
        return true;
      })
      .catch(function () {
        isSyncingDiscount = false;
        lastPassFailed = true;
        // A PARTIAL failure (clear landed, arm didn't) changed server state — one
        // refresh so the UI re-syncs to server truth instead of lying.
        if (mutated) { lastPassMutated = true; if (wantRefresh(opts)) refreshCart(); }
        return false;
      });
  }

  // Persist the resolved SESSION offer to cart.attributes._offer — the durable,
  // order-bound carrier (attribution / offer:<id>). Reads the SESSION layer
  // (window.__sessionOffer), never the display offer — a triggered cart campaign
  // (builder set) must not stop the sitewide session from persisting (sweep C2:
  // reading __activeOffer skipped persistence whenever a bundle was in cart).
  // Only once the cart is non-empty (avoids a cart-per-visitor; sessionStorage
  // covers pre-cart stickiness). Idempotent — once _offer matches, no-op.
  function persistSessionOffer(cart, opts) {
    var offer = (typeof window !== 'undefined') && window.__sessionOffer;
    if (!offer || !cart || !cart.items || !cart.items.length) return Promise.resolve(false);
    var by = offer.resolvedBy;
    if (by !== 'session' && by !== 'landing' && by !== 'default') return Promise.resolve(false);
    var current = (cart.attributes || {})._offer || null;
    if (current === offer.id) return Promise.resolve(false);
    if (typeof fetch !== 'function') return Promise.resolve(false);
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _offer: offer.id } })
    })
      .then(assertOk)
      .then(function () {
        if (wantRefresh(opts)) refreshCart();
        return true;
      })
      .catch(function () { lastPassFailed = true; return false; });
  }

  // ── GWP: add the gift variant for the active offer (the second primitive) ──
  // gwp_free_variant ($0) or gwp_coded (real-priced + a code that prices it). We
  // manage ONLY lines we stamp (_offer_gift = offer id) — never the legacy milestone
  // gift lines (which lack _offer_gift), so the two engines don't fight. The gift is
  // added once the PURCHASE subtotal (cart minus our gift lines) meets
  // reward.min_subtotal (allium $100; wavy 0 = any). gwp_coded REFUSES to add without
  // its code (Henya's footgun: no code -> customer charged).
  var GIFT_REWARD_TYPES = { gwp_free_variant: 1, gwp_coded: 1 };
  var isSyncingGift = false;

  function purchaseSubtotal(items) {
    var sum = 0;
    (items || []).forEach(function (i) {
      if ((i.properties || {})._offer_gift) return; // exclude our gifts from the threshold
      sum += i.final_line_price != null ? i.final_line_price : i.line_price || 0;
    });
    return sum;
  }

  function wantedGift(offer, items) {
    if (!offer || !offer.reward || !GIFT_REWARD_TYPES[offer.reward.type] || !offer.reward.variant_id) return null;
    if (offer.reward.type === 'gwp_coded' && !(offer.reward.code && String(offer.reward.code).trim())) return null; // footgun guard
    // A gift needs a PURCHASE: at least one non-gift line. Without this, a
    // zero-threshold GWP (baby-wavy) gifts an EMPTY cart and the un-removable
    // gift line makes the cart un-emptyable. (Sweep finding, 2026-08-11.)
    var hasPurchase = (items || []).some(function (i) { return !(i.properties || {})._offer_gift; });
    if (!hasPurchase) return null;
    if (purchaseSubtotal(items) < (offer.reward.min_subtotal || 0)) return null;
    return { variantId: String(offer.reward.variant_id), offerId: offer.id };
  }

  // The gift keys off the SITEWIDE SESSION layer (Sean's model, 2026-08-11): a
  // builder set stacks with the GWP because the session is still the GWP; a
  // preconfig-bundle session gets no gift because its blackout landing REPLACED
  // the sitewide offer. A cart campaign can additionally opt out via its
  // suppression list (naming the session offer's id or 'gwp') — config, not code.
  function giftOffer() {
    if (typeof window === 'undefined') return null;
    var session = window.__sessionOffer;
    if (!session) return null;
    // "the offer whose gift applies" - a non-GWP session (e.g. a blackout page's
    // fixed-bundle offer) has no gift by definition.
    var rt = session.reward && session.reward.type;
    if (rt !== 'gwp_free_variant' && rt !== 'gwp_coded') return null;
    var campaign = window.__activeOffer;
    if (campaign && campaign.id !== session.id) {
      var sup = campaign.suppression || [];
      if (sup.indexOf(session.id) !== -1 || sup.indexOf('gwp') !== -1) return null;
    }
    return session;
  }

  function syncGift(cart, opts) {
    if (isSyncingGift) return Promise.resolve(false);
    var items = (cart && cart.items) || [];
    var ourGifts = items.filter(function (i) { return (i.properties || {})._offer_gift; });
    var want = wantedGift(giftOffer(), items);
    var wantedLines = want ? ourGifts.filter(function (i) {
      return String(i.variant_id) === want.variantId && (i.properties || {})._offer_gift === want.offerId;
    }) : [];
    var haveWanted = wantedLines.length > 0;
    var toRemove = ourGifts.filter(function (i) {
      return !want || String(i.variant_id) !== want.variantId || (i.properties || {})._offer_gift !== want.offerId;
    });
    // Self-heal a raced double-add: the wanted gift must be qty 1 (a stale-snapshot
    // race can add.js the same line twice -> Shopify increments qty).
    var toFixQty = wantedLines.filter(function (i) { return (i.quantity || 1) > 1; });
    // NOTHING TO DO -> false, and crucially NO cart:refresh (a previous version
    // fired refresh unconditionally -> an infinite request loop; probe-caught).
    var needAdd = !!(want && !haveWanted);
    if (!needAdd && !toRemove.length && !toFixQty.length) return Promise.resolve(false);
    if (typeof fetch !== 'function') return Promise.resolve(false);

    isSyncingGift = true;
    var mutated = false;
    var chain = Promise.resolve();
    toRemove.concat(toFixQty).forEach(function (i) {
      var qty = toFixQty.indexOf(i) === -1 ? 0 : 1;
      chain = chain.then(function () {
        return fetch('/cart/change.js', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: i.key, quantity: qty })
        }).then(assertOk).then(function () { mutated = true; });
      });
    });
    if (needAdd) {
      chain = chain.then(function () {
        return fetch('/cart/add.js', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: want.variantId, quantity: 1, properties: { _gift_item: 'true', _offer_gift: want.offerId } }] })
        }).then(assertOk).then(function () { mutated = true; });
      });
    }
    return chain.then(function () {
      isSyncingGift = false;
      if (wantRefresh(opts)) refreshCart();
      return true;
    }).catch(function () {
      isSyncingGift = false;
      lastPassFailed = true;
      if (mutated) { lastPassMutated = true; if (wantRefresh(opts)) refreshCart(); }
      return false;
    });
  }

  // ── THE ORCHESTRATOR: one pass, sequential writers, one refresh, a breaker ──
  // Called by the drawer on every cart change. Runs discount -> gift -> attribute
  // IN ORDER (no interleaving -> no stale-snapshot double-writes), fires at most
  // ONE cart:refresh (only if something actually changed server-side), and trips
  // a breaker after MAX_FAIL_PASSES consecutive failing passes so a persistent
  // failure (sold-out gift, deleted code, 429) can never loop.
  var MAX_FAIL_PASSES = 3;
  var failPasses = 0;
  var isSyncingAll = false;
  var lastPassFailed = false;   // set by writers' catch handlers
  var lastPassMutated = false;  // set by writers when a partial failure still wrote

  function syncAll(cart) {
    if (isSyncingAll) return Promise.resolve(false);
    if (failPasses >= MAX_FAIL_PASSES) return Promise.resolve(false); // breaker open
    isSyncingAll = true;
    lastPassFailed = false;
    lastPassMutated = false;
    var noRefresh = { refresh: false };
    var didAny = false;
    return syncCartDiscount(cart, noRefresh)
      .then(function (a) { didAny = didAny || a; return syncGift(cart, noRefresh); })
      .then(function (b) { didAny = didAny || b; return persistSessionOffer(cart, noRefresh); })
      .then(function (c) {
        didAny = didAny || c;
        isSyncingAll = false;
        if (lastPassFailed) {
          failPasses++;
          if (failPasses >= MAX_FAIL_PASSES && typeof console !== 'undefined') {
            console.warn('[offer] cart writes failing repeatedly — offer sync paused for this session');
          }
        } else if (didAny) {
          failPasses = 0;
        }
        // one refresh per pass: on success-with-changes, or after a partial
        // failure that still changed server state (re-sync the UI to truth).
        if ((didAny && !lastPassFailed) || lastPassMutated) refreshCart();
        return didAny;
      })
      .catch(function () { isSyncingAll = false; failPasses++; return false; });
  }

  window.OfferReward = {
    checkoutCode: checkoutCode,
    checkoutUrl: checkoutUrl,
    activeOfferCode: activeOfferCode,
    codeInCart: codeInCart,
    syncCartDiscount: syncCartDiscount,
    persistSessionOffer: persistSessionOffer,
    wantedGift: wantedGift,
    giftOffer: giftOffer,
    syncGift: syncGift,
    syncAll: syncAll,
    bundleQty: bundleQty,
    tierFor: tierFor,
    managedCodes: managedCodes,
    clearCartDiscounts: clearCartDiscounts
  };
})();
